/**
 * Lazy loader for the Google Maps JS script.
 * Only loads once (idempotent). Returns a promise that resolves with
 * `window.google.maps` when ready, or rejects if the API key is missing.
 *
 * Usage:
 *   import { loadGoogleMaps } from "../lib/googleMaps";
 *   loadGoogleMaps().then((g) => { ... });
 */

let _loaderPromise = null;

export function loadGoogleMaps() {
    if (typeof window === "undefined") return Promise.reject(new Error("no-window"));
    if (window.google?.maps?.places) return Promise.resolve(window.google.maps);
    if (_loaderPromise) return _loaderPromise;

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
        return Promise.reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY not configured"));
    }

    _loaderPromise = new Promise((resolve, reject) => {
        const cbName = "__bbGmapsCb_" + Math.random().toString(36).slice(2);
        window[cbName] = () => {
            try {
                resolve(window.google.maps);
            } catch (e) {
                reject(e);
            } finally {
                delete window[cbName];
            }
        };

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=${cbName}&loading=async&v=weekly`;
        script.async = true;
        script.defer = true;
        script.onerror = (err) => {
            _loaderPromise = null;
            reject(err);
        };
        document.head.appendChild(script);
    });

    return _loaderPromise;
}

export function isGoogleMapsConfigured() {
    return !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
}
